const axios = require('axios')
const { getCookie, setCookie } = require('./utils/cookies')
const { generateRandomString } = require('./utils')
const { recordExperiment } = require('./utils/analytics')
const { log } = require('./utils/log')
const { getConfig } = require('./config')

class ZmartaExperiments {
  constructor ({ brand, country, environment, options, userAttributes }) {
    this.userId = ''
    this.userAgent = ''
    this.visitedUrl = ''
    this.config = getConfig(brand, country, environment)
    this.userAttributes = userAttributes || {}

    this.options = {
      ...this.config.defaultOptions,
      ...options || {}
    }

    if (this.options.userId) {
      this.userId = this.options.userId
    }

    if (this.options.userAgent) {
      this.userAgent = this.options.userAgent
    }

    if (this.options.visitedUrl) {
      this.visitedUrl = this.options.visitedUrl
    }

    if (this.options.setUserIdOnInit) {
      this.setUserId()
    }

    this.log('log', 'Initialized')
  }

  log (level, message, object) {
    if (this.options.enableLogging) {
      log(level, message, object)
    }
  }

  setUserId () {
    let userId = this.userId || getCookie('op_user_id')

    if (!userId) {
      userId = ZmartaExperiments.createUserId()
      setCookie('op_user_id', userId)
    }

    this.userId = userId

    this.log('log', `User id set to "${this.userId}"`)
  }

  /**
   * @returns {string}
   */
  static createUserId () {
    return generateRandomString()
  }

  /**
   * @param userAttributes
   */
  setUserAttributes (userAttributes) {
    this.userAttributes = userAttributes
  }

  /**
   * @returns {string|*|string}
   */
  getUserAgent () {
    return this.userAgent
      ? this.userAgent
      : typeof window === 'undefined' ? '' : window.navigator.userAgent
  }

  /**
   * @returns {string|*|string}
   */
  getVisitedUrl () {
    return this.visitedUrl
      ? this.visitedUrl
      : typeof window === 'undefined' ? '' : window.document.location.pathname
  }

  /**
   * @param userAttributes
   * @returns {{'op-user-attributes': string, 'op-user-id': (string|*), 'op-user-agent': (string|string), 'op-brand', 'op-visited-url': (string|string)}}
   */
  getHeaders ({ userAttributes }) {
    return {
      'op-brand': this.config.brand,
      'op-user-id': this.userId,
      'op-user-agent': this.getUserAgent(),
      'op-visited-url': this.getVisitedUrl(),
      'op-user-attributes': JSON.stringify({ ...(userAttributes || {}), ...(this.userAttributes || {}) })
    }
  }

  /**
   * @param experimentKey
   * @param userAttributes
   * @param zmartaAnalytics
   * @returns {Promise<null|*>}
   */
  async activate (experimentKey, userAttributes, zmartaAnalytics = null) {
    const url = `${this.config.gateway.url}/api/v1/experiments/${experimentKey}`

    let res = {}
    try {
      res = await axios.post(
        url,
        {},
        {
          headers: this.getHeaders({ userAttributes })
        }
      )
    } catch (e) {
      this.log('warn', `Unable to activate experiment "${experimentKey}"`)
      return null
    }

    if (res.data && res.data.error) {
      this.log('warn', `Did not activate experiment ${experimentKey}. Message: "${res.data.error}"`)
      return null
    }

    const experiment = res.data

    if (!experiment) {
      this.log('log', `Experiment "${experimentKey}" did not match audience. Check app.optimizely.com`)
      return null
    }

    this.log('log', `Activated experiment "${experimentKey}"`, experiment)

    if (this.options.gaEventOnActivate && zmartaAnalytics) {
      zmartaAnalytics.event.experiment.activated({
        experimentId: experiment.id,
        experimentKey: experiment.experimentKey,
        variationId: experiment.variationsMap[experiment.variationKey] && experiment.variationsMap[experiment.variationKey].id,
        variationKey: experiment.variationKey
      })
    }

    if (this.options.hotjarRecordingOnActivate && typeof document !== 'undefined') {
      const recordingName = recordExperiment({
        experimentKey: experiment.experimentKey,
        variationKey: experiment.variationKey,
        path: document.location.pathname
      })

      if (recordingName) {
        this.log('log', `Hotjar recording started for experiment "${experimentKey}"`, recordingName)
      } else {
        this.log('warn', `Missing data for activating Hotjar recording for experiment ${experimentKey}`)
      }
    }

    return experiment
  }

  /**
   * @param eventKey
   * @param userAttributes
   * @returns {Promise<boolean>}
   */
  async track (eventKey, userAttributes) {
    try {
      const url = `${this.config.gateway.url}/api/v1/experiments/events/${eventKey}`
      const res = await axios.post(
        url,
        {},
        {
          headers: this.getHeaders({ userAttributes })
        })

      if (res.data.error && this.options.enableLogging) {
        log('warn', `Could not track event "${eventKey}" - ${res.data.error}`)
      } else {
        this.log('log', `Tracked event "${eventKey}"`)
      }

      return !res.data.error
    } catch (e) {
      this.log('warn', `Could not track event "${eventKey}"`)
      return false
    }
  }

  /**
   * @param brand
   * @param gatewayUrl
   * @param userId
   * @param userAgent
   * @param visitedUrl
   * @param userIp
   * @param userAttributes
   * @returns {Promise<{}|any>}
   */
  async getVariationsForUser ({ brand, gatewayUrl, userId, userAgent, visitedUrl, userIp, userAttributes }) {
    try {
      let headers = {
        'op-brand': brand,
        'op-user-id': userId,
        'op-user-agent': userAgent,
        'op-visited-url': visitedUrl,
        'op-user-ip': userIp,
        'op-user-attributes': userAttributes || {}
      }

      headers = Object.keys(headers)
        .filter(key => headers[key])
        .reduce((_headers, name) => ({ ..._headers, [name]: headers[name] }), {})

      const response = await axios.get(
        `${gatewayUrl}/api/v1/experiments`,
        {
          headers
        })

      return response.data
    } catch (e) {
      console.error('ZMARTA_EXPERIMENTS: Variations could not be fetched')
      return {}
    }
  }
}

module.exports = ZmartaExperiments
